<div class="container">
  <div class="loading-overlay" *ngIf="(prescriberDetails$ | async)?.pending">
    <kendo-loader type="converging-spinner" size="large"></kendo-loader>
  </div>

  <div class="form-container" [formGroup]="form">
    <div class="control-wrap">
      <kendo-formfield>
        <kendo-label [for]="npiInput" text="Search by Prescriber NPI">
        </kendo-label>
        <kendo-textbox
          #npiInput
          class="form-field"
          formControlName="npi"
          (keydown.Enter)="getPrescriberDetails()"
          (keydown.Tab)="getPrescriberDetails()"
        >
        </kendo-textbox>
      </kendo-formfield>
    </div>
    <div class="control-wrap search-btn">
      <button kendoButton [primary]="true" (click)="getPrescriberDetails()">
        <span class="k-icon k-font-icon k-i-search"></span>
        Search
      </button>
      <p *ngIf="lastSearchDate" class="search-date">
        Last searched {{ lastSearchDate | date: dateTimeFormat }}
      </p>
    </div>
  </div>

  <ng-container *ngIf="searchSubmitted">
    <ng-container *ngIf="prescriberDetails$ | async as details">
      <div class="no-data-container" [class.container-hidden]="details.data">
        No prescriber data
      </div>

      <div [class.container-hidden]="!details.data">
        <section class="details-container">
          <div class="detail-field">
            <kendo-label class="detail-field__label" text="NPI"> </kendo-label>
            <div class="detail-field__textbox">
              <span>{{ details.data?.npi }}</span>
            </div>
          </div>

          <div class="detail-field span-3">
            <kendo-label class="detail-field__label" text="Name"> </kendo-label>
            <div class="detail-field__textbox">
              <span>{{ details.data?.name }}</span>
            </div>
          </div>
        </section>

        <section class="tabs-container">
          <kendo-tabstrip [keepTabContent]="true">
            <!-- Locations/Affiliations -->
            <kendo-tabstrip-tab title="Prescriber Details" [selected]="true">
              <ng-template kendoTabContent>
                <div class="tab-content">
                  <!-- CE Provided Data -->
                  <kendo-grid
                    class="detail-table"
                    [kendoGridBinding]="details.data?.ceData ?? []"
                    [loading]="false"
                    [pageable]="false"
                    [sortable]="true"
                    [filterable]="false"
                    [resizable]="true"
                  >
                    <ng-template kendoGridToolbarTemplate>
                      <div class="toolbar-container">
                        <div class="toolbar-container__left">
                          <span class="table-title">CE Provided Data</span>
                        </div>
                        <div class="toolbar-container__right"></div>
                      </div>
                    </ng-template>

                    <kendo-grid-column
                      title="CE Account ID"
                      field="ce_account_id"
                    >
                    </kendo-grid-column>

                    <kendo-grid-column title="340B ID" field="id340b">
                    </kendo-grid-column>

                    <kendo-grid-column title="Status" field="status">
                    </kendo-grid-column>

                    <kendo-grid-column title="Start Date" field="start_date">
                      <ng-template kendoGridCellTemplate let-dataItem>
                        {{ dataItem.start_date | date: dateFormat }}
                      </ng-template>
                    </kendo-grid-column>

                    <kendo-grid-column title="End Date" field="end_date">
                      <ng-template kendoGridCellTemplate let-dataItem>
                        {{ dataItem.end_date | date: dateFormat }}
                      </ng-template>
                    </kendo-grid-column>
                  </kendo-grid>

                  <!-- Locations -->
                  <kendo-grid
                    #locationsGrid
                    class="detail-table rb-grid-row-detail"
                    [kendoGridBinding]="details.data?.locations ?? []"
                    [loading]="false"
                    [pageable]="false"
                    [sortable]="true"
                    [filterable]="false"
                    [resizable]="true"
                  >
                    <ng-template kendoGridToolbarTemplate>
                      <div class="toolbar-container">
                        <div class="toolbar-container__left">
                          <span class="table-title">Locations</span>
                        </div>
                        <div class="toolbar-container__right">
                          <button
                            kendoButton
                            (click)="
                              expandAllRows(
                                locationsGrid,
                                details.data?.locations?.length ?? 0
                              )
                            "
                          >
                            Expand All
                          </button>
                          <button
                            kendoButton
                            (click)="
                              collapseAllRows(
                                locationsGrid,
                                details.data?.locations?.length ?? 0
                              )
                            "
                          >
                            Collapse All
                          </button>
                        </div>
                      </div>
                    </ng-template>

                    <kendo-grid-column
                      title="Address Line 1"
                      field="address.StreetAddress1"
                    >
                    </kendo-grid-column>

                    <kendo-grid-column
                      title="Address Line 2"
                      field="address.StreetAddress2"
                    >
                    </kendo-grid-column>

                    <kendo-grid-column title="City" field="address.City">
                    </kendo-grid-column>

                    <kendo-grid-column title="State" field="address.State">
                    </kendo-grid-column>

                    <kendo-grid-column title="Zipcode" field="address.Zip5">
                      <ng-template kendoGridCellTemplate let-dataItem>
                        {{
                          dataItem.address?.Zip5 && dataItem.address?.Zip4
                            ? dataItem.address?.Zip5 +
                              '-' +
                              dataItem.address?.Zip4
                            : dataItem.address?.Zip5
                        }}
                      </ng-template>
                    </kendo-grid-column>

                    <!-- Location - Matching CEs -->
                    <ng-template
                      kendoGridDetailTemplate
                      let-dataItem
                      [kendoGridDetailTemplateShowIf]="showLocationDetail"
                    >
                      <kendo-grid
                        class="detail-table row-detail-table"
                        [kendoGridBinding]="dataItem.matchingCEs ?? []"
                        [loading]="false"
                        [pageable]="false"
                        [sortable]="true"
                        [filterable]="false"
                        [resizable]="true"
                      >
                        <kendo-grid-column
                          title="CE ID"
                          field="associated_340B_CE_ID"
                        >
                        </kendo-grid-column>

                        <kendo-grid-column title="Name" field="ce_name">
                        </kendo-grid-column>

                        <kendo-grid-column title="Address" field="full_address">
                        </kendo-grid-column>

                        <kendo-grid-column
                          title="Confidence"
                          field="confidence_340b"
                        >
                          <ng-template kendoGridCellTemplate let-dataItem>
                            {{ dataItem.confidence_340b | confidence340b }}
                          </ng-template>
                        </kendo-grid-column>

                        <kendo-grid-column
                          title="Start Date"
                          field="start_date_of_340B_association"
                        >
                          <ng-template kendoGridCellTemplate let-dataItem>
                            {{
                              dataItem.start_date_of_340B_association
                                | date: dateFormat
                            }}
                          </ng-template>
                        </kendo-grid-column>

                        <kendo-grid-column
                          title="End Date"
                          field="end_date_of_340B_association"
                        >
                          <ng-template kendoGridCellTemplate let-dataItem>
                            {{
                              dataItem.end_date_of_340B_association
                                | date: dateFormat
                            }}
                          </ng-template>
                        </kendo-grid-column>
                      </kendo-grid>
                    </ng-template>
                  </kendo-grid>

                  <!-- Affiliations -->
                  <kendo-grid
                    class="detail-table rb-grid-row-detail"
                    [kendoGridBinding]="details.data?.affiliations ?? []"
                    [loading]="false"
                    [pageable]="false"
                    [sortable]="true"
                    [filterable]="false"
                    [resizable]="true"
                  >
                    <ng-template kendoGridToolbarTemplate>
                      <div class="toolbar-container">
                        <div class="toolbar-container__left">
                          <span class="table-title">Affiliations</span>
                        </div>
                        <div class="toolbar-container__right"></div>
                      </div>
                    </ng-template>

                    <kendo-grid-column title="Name" field="name">
                    </kendo-grid-column>

                    <kendo-grid-column title="Type" field="affiliationType">
                    </kendo-grid-column>

                    <kendo-grid-column
                      title="Address Line 1"
                      field="address.StreetAddress1"
                    >
                    </kendo-grid-column>

                    <kendo-grid-column
                      title="Address Line 2"
                      field="address.StreetAddress2"
                    >
                    </kendo-grid-column>

                    <kendo-grid-column title="City" field="address.City">
                    </kendo-grid-column>

                    <kendo-grid-column title="State" field="address.State">
                    </kendo-grid-column>

                    <kendo-grid-column title="Zipcode" field="address.Zip5">
                      <ng-template kendoGridCellTemplate let-dataItem>
                        {{
                          dataItem.address?.Zip5 && dataItem.address?.Zip4
                            ? dataItem.address?.Zip5 +
                              '-' +
                              dataItem.address?.Zip4
                            : dataItem.address?.Zip5
                        }}
                      </ng-template>
                    </kendo-grid-column>

                    <ng-template kendoGridDetailTemplate let-dataItem>
                      <kendo-tabstrip>
                        <!-- Affiliation - Matching CEs -->
                        <kendo-tabstrip-tab
                          title="Covered Entities"
                          [selected]="true"
                        >
                          <ng-template kendoTabContent>
                            <div class="tab-content">
                              <div class="row">
                                <kendo-grid
                                  class="detail-table row-detail-table"
                                  [kendoGridBinding]="
                                    dataItem.matchingCEsByLN ?? []
                                  "
                                  [loading]="false"
                                  [pageable]="false"
                                  [sortable]="true"
                                  [filterable]="false"
                                  [resizable]="true"
                                >
                                  <ng-template kendoGridToolbarTemplate>
                                    <div class="toolbar-container">
                                      <div class="toolbar-container__left">
                                        <span class="table-title"
                                          >Matching Covered Entities by
                                          LexisNexis</span
                                        >
                                      </div>
                                      <div
                                        class="toolbar-container__right"
                                      ></div>
                                    </div>
                                  </ng-template>

                                  <kendo-grid-column
                                    title="CE ID"
                                    field="associated_340B_CE_ID"
                                  >
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Parent CE ID"
                                    field="associated_340B_parent_CE_ID"
                                  >
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Confidence"
                                    field="confidence_340b"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.confidence_340b
                                          | confidence340b
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Start Date"
                                    field="start_date_of_340B_association"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.start_date_of_340B_association
                                          | date: dateFormat
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="End Date"
                                    field="end_date_of_340B_association"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.end_date_of_340B_association
                                          | date: dateFormat
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>
                                </kendo-grid>

                                <kendo-grid
                                  class="detail-table row-detail-table"
                                  [kendoGridBinding]="
                                    dataItem.matchingCEsWithLocationAddress ??
                                    []
                                  "
                                  [loading]="false"
                                  [pageable]="false"
                                  [sortable]="true"
                                  [filterable]="false"
                                  [resizable]="true"
                                >
                                  <ng-template kendoGridToolbarTemplate>
                                    <div class="toolbar-container">
                                      <div class="toolbar-container__left">
                                        <span class="table-title"
                                          >Matching Covered Entities with
                                          Location Address</span
                                        >
                                      </div>
                                      <div
                                        class="toolbar-container__right"
                                      ></div>
                                    </div>
                                  </ng-template>

                                  <kendo-grid-column
                                    title="CE ID"
                                    field="associated_340B_CE_ID"
                                  >
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Parent CE ID"
                                    field="associated_340B_parent_CE_ID"
                                  >
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Confidence"
                                    field="confidence_340b"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.confidence_340b
                                          | confidence340b
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="Start Date"
                                    field="start_date_of_340B_association"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.start_date_of_340B_association
                                          | date: dateFormat
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>

                                  <kendo-grid-column
                                    title="End Date"
                                    field="end_date_of_340B_association"
                                  >
                                    <ng-template
                                      kendoGridCellTemplate
                                      let-dataItem
                                    >
                                      {{
                                        dataItem.end_date_of_340B_association
                                          | date: dateFormat
                                      }}
                                    </ng-template>
                                  </kendo-grid-column>
                                </kendo-grid>
                              </div>
                            </div>
                          </ng-template>
                        </kendo-tabstrip-tab>

                        <!-- Affiliation - Affiliations -->
                        <kendo-tabstrip-tab title="Affiliations">
                          <ng-template kendoTabContent>
                            <div class="tab-content">
                              <kendo-grid
                                class="detail-table row-detail-table rb-grid-row-detail"
                                [kendoGridBinding]="dataItem.affiliations ?? []"
                                [loading]="false"
                                [pageable]="false"
                                [sortable]="true"
                                [filterable]="false"
                                [resizable]="true"
                              >
                                <kendo-grid-column title="Name" field="name">
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="Type"
                                  field="affiliationType"
                                >
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="Skipped"
                                  field="skipped"
                                >
                                  <ng-template
                                    kendoGridCellTemplate
                                    let-dataItem
                                  >
                                    {{ dataItem.skipped | booleanYesNo }}
                                  </ng-template>
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="Address Line 1"
                                  field="address.StreetAddress1"
                                >
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="Address Line 2"
                                  field="address.StreetAddress2"
                                >
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="City"
                                  field="address.City"
                                >
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="State"
                                  field="address.State"
                                >
                                </kendo-grid-column>

                                <kendo-grid-column
                                  title="Zipcode"
                                  field="address.Zip5"
                                >
                                  <ng-template
                                    kendoGridCellTemplate
                                    let-dataItem
                                  >
                                    {{
                                      dataItem.address?.Zip5 &&
                                      dataItem.address?.Zip4
                                        ? dataItem.address?.Zip5 +
                                          '-' +
                                          dataItem.address?.Zip4
                                        : dataItem.address?.Zip5
                                    }}
                                  </ng-template>
                                </kendo-grid-column>

                                <ng-template
                                  kendoGridDetailTemplate
                                  let-dataItem
                                >
                                  <kendo-tabstrip>
                                    <!-- Affiliation - Affiliation - Matching CEs -->
                                    <kendo-tabstrip-tab
                                      title="Covered Entities"
                                      [selected]="true"
                                    >
                                      <ng-template kendoTabContent>
                                        <div class="tab-content">
                                          <kendo-grid
                                            class="detail-table row-detail-table"
                                            [kendoGridBinding]="
                                              dataItem.matchingCEsWithAffiliationAddress ??
                                              []
                                            "
                                            [loading]="false"
                                            [pageable]="false"
                                            [sortable]="true"
                                            [filterable]="false"
                                            [resizable]="true"
                                          >
                                            <ng-template
                                              kendoGridToolbarTemplate
                                            >
                                              <div class="toolbar-container">
                                                <div
                                                  class="toolbar-container__left"
                                                >
                                                  <span class="table-title"
                                                    >Matching Covered Entities
                                                    with Affiliation
                                                    Address</span
                                                  >
                                                </div>
                                                <div
                                                  class="toolbar-container__right"
                                                ></div>
                                              </div>
                                            </ng-template>

                                            <kendo-grid-column
                                              title="CE ID"
                                              field="associated_340B_CE_ID"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Parent CE ID"
                                              field="associated_340B_parent_CE_ID"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Confidence"
                                              field="confidence_340b"
                                            >
                                              <ng-template
                                                kendoGridCellTemplate
                                                let-dataItem
                                              >
                                                {{
                                                  dataItem.confidence_340b
                                                    | confidence340b
                                                }}
                                              </ng-template>
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Start Date"
                                              field="start_date_of_340B_association"
                                            >
                                              <ng-template
                                                kendoGridCellTemplate
                                                let-dataItem
                                              >
                                                {{
                                                  dataItem.start_date_of_340B_association
                                                    | date: dateFormat
                                                }}
                                              </ng-template>
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="End Date"
                                              field="end_date_of_340B_association"
                                            >
                                              <ng-template
                                                kendoGridCellTemplate
                                                let-dataItem
                                              >
                                                {{
                                                  dataItem.end_date_of_340B_association
                                                    | date: dateFormat
                                                }}
                                              </ng-template>
                                            </kendo-grid-column>
                                          </kendo-grid>
                                        </div>
                                      </ng-template>
                                    </kendo-tabstrip-tab>

                                    <!-- Affiliation - Affiliation - Affiliations -->
                                    <kendo-tabstrip-tab title="Affiliations">
                                      <ng-template kendoTabContent>
                                        <div class="tab-content">
                                          <kendo-grid
                                            class="detail-table row-detail-table rb-grid-row-detail"
                                            [kendoGridBinding]="
                                              dataItem.affiliations ?? []
                                            "
                                            [loading]="false"
                                            [pageable]="false"
                                            [sortable]="true"
                                            [filterable]="false"
                                            [resizable]="true"
                                          >
                                            <kendo-grid-column
                                              title="Name"
                                              field="name"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Type"
                                              field="affiliationType"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Skipped"
                                              field="skipped"
                                            >
                                              <ng-template
                                                kendoGridCellTemplate
                                                let-dataItem
                                              >
                                                {{
                                                  dataItem.skipped
                                                    | booleanYesNo
                                                }}
                                              </ng-template>
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Address Line 1"
                                              field="address.StreetAddress1"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Address Line 2"
                                              field="address.StreetAddress2"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="City"
                                              field="address.City"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="State"
                                              field="address.State"
                                            >
                                            </kendo-grid-column>

                                            <kendo-grid-column
                                              title="Zipcode"
                                              field="address.Zip5"
                                            >
                                              <ng-template
                                                kendoGridCellTemplate
                                                let-dataItem
                                              >
                                                {{
                                                  dataItem.address?.Zip5 &&
                                                  dataItem.address?.Zip4
                                                    ? dataItem.address?.Zip5 +
                                                      '-' +
                                                      dataItem.address?.Zip4
                                                    : dataItem.address?.Zip5
                                                }}
                                              </ng-template>
                                            </kendo-grid-column>
                                          </kendo-grid>
                                        </div>
                                      </ng-template>
                                    </kendo-tabstrip-tab>
                                  </kendo-tabstrip>
                                </ng-template>
                              </kendo-grid>
                            </div>
                          </ng-template>
                        </kendo-tabstrip-tab>
                      </kendo-tabstrip>
                    </ng-template>
                  </kendo-grid>

                  <!-- Affiliated CE -->
                  <kendo-grid
                    class="detail-table"
                    [kendoGridBinding]="details.data?.affiliatedCE ?? []"
                    [loading]="false"
                    [pageable]="false"
                    [sortable]="true"
                    [filterable]="false"
                    [resizable]="true"
                  >
                    <ng-template kendoGridToolbarTemplate>
                      <div class="toolbar-container">
                        <div class="toolbar-container__left">
                          <span class="table-title"
                            >Affiliated Covered Entities</span
                          >
                        </div>
                        <div class="toolbar-container__right"></div>
                      </div>
                    </ng-template>

                    <kendo-grid-column title="340B ID" field="id340b">
                    </kendo-grid-column>

                    <kendo-grid-column title="Name" field="ce_name">
                    </kendo-grid-column>

                    <kendo-grid-column
                      title="Address"
                      field="address_original.full_address"
                    >
                    </kendo-grid-column>
                  </kendo-grid>
                </div>
              </ng-template>
            </kendo-tabstrip-tab>

            <!-- Manual CE Associations -->
            <kendo-tabstrip-tab title="Manual CE Associations">
              <ng-template kendoTabContent>
                <div class="tab-content">
                  <div class="row">
                    <kendo-grid
                      class="detail-table manual-added-ce-table"
                      [kendoGridBinding]="details.data?.manualAddedCE ?? []"
                      [loading]="false"
                      [pageable]="false"
                      [sortable]="true"
                      [filterable]="false"
                      [resizable]="true"
                      (add)="addCEAssociation()"
                      (remove)="removeCEAssociation($event, 'add')"
                    >
                      <ng-template kendoGridToolbarTemplate>
                        <div class="toolbar-container">
                          <div class="toolbar-container__left">
                            <span class="table-title"
                              >Manually Added CE Associations</span
                            >
                          </div>
                          <div class="toolbar-container__right">
                            <ng-container *appRole="editRoles">
                              <button
                                kendoGridAddCommand
                                [disabled]="!details.data"
                              >
                                Add CE Association
                              </button>
                            </ng-container>
                          </div>
                        </div>
                      </ng-template>

                      <kendo-grid-column
                        title="CE ID"
                        field="associated_340B_CE_ID"
                      >
                      </kendo-grid-column>

                      <kendo-grid-column
                        title="Parent CE ID"
                        field="associated_340B_parent_CE_ID"
                      >
                      </kendo-grid-column>

                      <kendo-grid-column
                        title="Confidence"
                        field="confidence_340b"
                      >
                        <ng-template kendoGridCellTemplate let-dataItem>
                          {{ dataItem.confidence_340b | confidence340b }}
                        </ng-template>
                      </kendo-grid-column>

                      <kendo-grid-column
                        title="Start Date"
                        field="start_date_of_340B_association"
                      >
                        <ng-template kendoGridCellTemplate let-dataItem>
                          {{
                            dataItem.start_date_of_340B_association
                              | date: dateFormat
                          }}
                        </ng-template>
                      </kendo-grid-column>

                      <kendo-grid-column
                        title="End Date"
                        field="end_date_of_340B_association"
                      >
                        <ng-template kendoGridCellTemplate let-dataItem>
                          {{
                            dataItem.end_date_of_340B_association
                              | date: dateFormat
                          }}
                        </ng-template>
                      </kendo-grid-column>

                      <kendo-grid-command-column
                        class="k-text-center"
                        [width]="90"
                        *appRole="editRoles"
                      >
                        <ng-template kendoGridCellTemplate let-dataItem>
                          <button
                            kendoGridRemoveCommand
                            [disabled]="
                              !dataItem.id || !dataItem.associated_340B_CE_ID
                            "
                          >
                            Remove
                          </button>
                        </ng-template>
                      </kendo-grid-command-column>
                    </kendo-grid>

                    <kendo-grid
                      class="detail-table manual-removed-ce-table"
                      [kendoGridBinding]="details.data?.manualRemovedCE ?? []"
                      [loading]="false"
                      [pageable]="false"
                      [sortable]="true"
                      [filterable]="false"
                      [resizable]="true"
                      (add)="addCEAssociationRemoval()"
                      (remove)="removeCEAssociation($event, 'remove')"
                    >
                      <ng-template kendoGridToolbarTemplate>
                        <div class="toolbar-container">
                          <div class="toolbar-container__left">
                            <span class="table-title"
                              >Manually Removed CE Associations</span
                            >
                          </div>
                          <div class="toolbar-container__right">
                            <ng-container *appRole="editRoles">
                              <button
                                kendoGridAddCommand
                                [disabled]="!details.data"
                              >
                                Add CE Association
                              </button>
                            </ng-container>
                          </div>
                        </div>
                      </ng-template>

                      <kendo-grid-column
                        title="CE ID"
                        field="associated_340B_CE_ID"
                      >
                      </kendo-grid-column>

                      <kendo-grid-command-column
                        class="k-text-center"
                        [width]="90"
                        *appRole="editRoles"
                      >
                        <ng-template kendoGridCellTemplate let-dataItem>
                          <button
                            kendoGridRemoveCommand
                            [disabled]="!dataItem.id"
                          >
                            Remove
                          </button>
                        </ng-template>
                      </kendo-grid-command-column>
                    </kendo-grid>
                  </div>
                </div>
              </ng-template>
            </kendo-tabstrip-tab>
          </kendo-tabstrip>
        </section>
      </div>
    </ng-container>
  </ng-container>
</div>
